<template>
  <article
    class="kanban-card"
    :class="cardClasses"
    :draggable="draggable"
    tabindex="0"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
    @dragover.prevent="handleDragOver"
    @click="emitOpen"
    @keydown.enter="emitOpen"
  >
    <header class="card-header">
      <div class="card-title">
        <span class="drag-handle" aria-hidden="true"></span>
        <h4>{{ task.title }}</h4>
      </div>
      <div class="card-actions">
        <button
          class="icon-btn"
          type="button"
          @click.stop="emitDuplicate"
          aria-label="Duplicar tarea"
        >
          Dup
        </button>
        <label class="checkbox" @click.stop>
          <input type="checkbox" :checked="task.done" @change="emitToggleDone" />
          <span></span>
        </label>
      </div>
    </header>

    <div class="card-chips">
      <span v-if="task.projectName" class="chip project">{{ task.projectName }}</span>
      <span v-if="priorityLabel" class="chip priority" :class="priorityClass">
        {{ priorityLabel }}
      </span>
    </div>

    <p v-if="task.description" class="card-desc">{{ task.description }}</p>

    <div v-if="tags && tags.length" class="card-tags">
      <span v-for="tag in tags" :key="tag" class="tag">#{{ tag }}</span>
    </div>

    <div class="card-footer">
      <span v-if="dueLabel" class="due" :class="{ overdue }">Vence {{ dueLabel }}</span>
      <span v-if="assigneeLabel" class="assignee" :title="assigneeLabel">
        {{ assigneeInitials }}
      </span>
      <div v-if="progress != null" class="progress">
        <div class="progress-bar">
          <span :style="{ width: `${progress}%` }"></span>
        </div>
        <span class="progress-value">{{ progress }}%</span>
      </div>
    </div>
  </article>
</template>

<script>
export default {
  name: "KanbanCard",
  props: {
    task: { type: Object, required: true },
    draggable: { type: Boolean, default: true },
    isDragging: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
    priorityLabel: { type: String, default: "" },
    priorityClass: { type: String, default: "" },
    tags: { type: Array, default: () => [] },
    dueLabel: { type: String, default: "" },
    overdue: { type: Boolean, default: false },
    assigneeLabel: { type: String, default: "" },
    progress: { type: Number, default: null },
  },
  emits: ["open", "toggle-done", "duplicate", "dragstart", "dragend", "dragover"],
  computed: {
    cardClasses() {
      return [
        this.priorityClass,
        {
          dragging: this.isDragging,
          done: this.task.done,
          compact: this.compact,
          overdue: this.overdue,
        },
      ];
    },
    assigneeInitials() {
      const label = this.assigneeLabel || "";
      const parts = label.trim().split(" ").filter(Boolean);
      if (!parts.length) return "";
      const first = parts[0]?.[0] || "";
      const second = parts[1]?.[0] || "";
      return `${first}${second}`.toUpperCase();
    },
  },
  methods: {
    emitOpen() {
      this.$emit("open");
    },
    emitToggleDone() {
      this.$emit("toggle-done");
    },
    emitDuplicate() {
      this.$emit("duplicate");
    },
    handleDragStart(event) {
      this.$emit("dragstart", event);
    },
    handleDragEnd() {
      this.$emit("dragend");
    },
    handleDragOver() {
      this.$emit("dragover");
    },
  },
};
</script>

<style scoped>
.kanban-card {
  background: var(--card-bg);
  border: 1px solid var(--border-soft, rgba(148, 163, 184, 0.2));
  border-radius: 16px;
  padding: 12px;
  display: grid;
  gap: 8px;
  cursor: grab;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.kanban-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px rgba(5, 10, 25, 0.4);
  border-color: rgba(56, 189, 248, 0.45);
}

.kanban-card:focus-visible {
  outline: 2px solid rgba(56, 189, 248, 0.7);
  outline-offset: 2px;
}

.kanban-card.dragging {
  opacity: 0.7;
  transform: scale(0.98);
}

.kanban-card.done {
  opacity: 0.6;
}

.kanban-card.done h4 {
  text-decoration: line-through;
}

.kanban-card.compact {
  padding: 10px;
  gap: 6px;
}

.kanban-card.overdue {
  border-color: rgba(248, 113, 113, 0.5);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.card-title {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.card-title h4 {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 600;
  word-break: break-word;
}

.drag-handle {
  width: 14px;
  height: 18px;
  flex: 0 0 auto;
  opacity: 0.55;
  background-image: radial-gradient(circle, rgba(148, 163, 184, 0.7) 1.5px, transparent 1.6px);
  background-size: 6px 6px;
  background-position: 0 0;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.2;
  transition: opacity 0.2s ease;
}

.kanban-card:hover .card-actions,
.kanban-card:focus-within .card-actions {
  opacity: 1;
}

.icon-btn {
  background: rgba(148, 163, 184, 0.14);
  border: 1px solid rgba(148, 163, 184, 0.3);
  color: var(--text);
  width: 28px;
  height: 28px;
  border-radius: 10px;
  cursor: pointer;
  display: grid;
  place-items: center;
  font-size: 12px;
}

.card-chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.chip {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(148, 163, 184, 0.12);
  color: var(--text);
}

.chip.project {
  color: var(--accent);
  border-color: rgba(56, 189, 248, 0.35);
  background: rgba(56, 189, 248, 0.12);
}

.chip.priority.priority-high {
  color: #fca5a5;
  border-color: rgba(248, 113, 113, 0.45);
  background: rgba(248, 113, 113, 0.16);
}

.chip.priority.priority-medium {
  color: #fcd34d;
  border-color: rgba(251, 191, 36, 0.45);
  background: rgba(251, 191, 36, 0.16);
}

.chip.priority.priority-low {
  color: #7dd3fc;
  border-color: rgba(56, 189, 248, 0.45);
  background: rgba(56, 189, 248, 0.14);
}

.card-desc {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.kanban-card:hover .card-desc,
.kanban-card:focus-within .card-desc {
  -webkit-line-clamp: 4;
}

.card-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 11px;
  color: var(--muted);
}

.tag {
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.14);
  border: 1px solid rgba(148, 163, 184, 0.24);
}

.card-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.due {
  font-size: 11px;
  color: var(--muted);
}

.due.overdue {
  color: var(--danger);
}

.assignee {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(56, 189, 248, 0.18);
  border: 1px solid rgba(56, 189, 248, 0.3);
  color: var(--text);
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 600;
}

.progress {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 120px;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: rgba(148, 163, 184, 0.2);
  border-radius: 999px;
  overflow: hidden;
}

.progress-bar span {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, var(--accent), var(--success));
}

.progress-value {
  font-size: 11px;
  color: var(--muted);
}

.checkbox {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.checkbox input {
  display: none;
}

.checkbox span {
  width: 16px;
  height: 16px;
  border: 1px solid var(--border);
  border-radius: 4px;
  display: inline-block;
  position: relative;
}

.checkbox input:checked + span {
  background: rgba(34, 197, 94, 0.4);
  border-color: rgba(34, 197, 94, 0.6);
}

@media (max-width: 600px) {
  .kanban-card {
    padding: 10px;
  }

  .progress {
    min-width: 90px;
  }
}
</style>
